import { Fragment, useEffect, useState } from "react";

import { CommandAcceptOpsFlag } from "../commands/cmdAcceptOpsFlag";
import { CommandGetOpsTile } from "../commands/cmdGetOpsTile";
import styles from "../opsDashboard.module.css";
import {
    filtersActive, isToned, plural, TILE_SPECS, unitLabel,
} from "../opsDashboard.types";
import type {
    Column, OpsAcceptance, OpsAlertRow, OpsFilters, OpsTile, PayloadValue,
} from "../opsDashboard.types";
import { rowHref } from "../rowLink";
import { Badge } from "./Badge";
import { EmptyState } from "./EmptyState";
import { InfoTip } from "./InfoTip";

/** The mockup shows three rows, then a "Show all N" expander. */
const COLLAPSED_ROWS = 3;


const CELL_CLASS: Record<Column["kind"], string> = {
    link: styles.cellLink,
    text: styles.cellText,
    mono: styles.cellMono,
    muted: styles.cellMuted,
    number: styles.cellNumber,
    badge: styles.cellBadge,
    progress: styles.cellProgress,
};

function Cell({
    column, value, href, pct,
}: { column: Column; value: PayloadValue; href: string | null; pct?: number }) {
    if (column.kind === "progress") {
        const text = isToned(value) ? value.t : (value ?? "—");
        const done = Math.max(0, Math.min(100, pct ?? 0));
        return (
            <span className={styles.progress}>
                {/* The bar shows how much is DONE while the label counts what is LEFT, so
                    it fills as the campaign gets closer to live. Same 4px track and green
                    fill as the engagement workflow sidebar, which measures the same steps. */}
                <span className={styles.progressTrack}>
                    <span className={styles.progressFill} style={{ width: `${done}%` }} />
                </span>
                <span className={styles.progressLabel}>{text}</span>
            </span>
        );
    }

    if (column.kind === "badge") {
        if (!isToned(value)) return <>{value ?? "—"}</>;
        return <Badge tone={value.tone}>{value.t}</Badge>;
    }

    const text = isToned(value) ? value.t : (value ?? "—");

    if (column.kind === "link") {
        // No destination means no link: a styled anchor that goes nowhere reads as broken.
        if (!href) return <>{text}</>;
        return (
            <>
                {/* New window is a confirmed requirement, not a preference (MPN-1840):
                    the team works multi-tab and the platform has no right-click
                    open-in-new-tab support (MPN-1869). `rel` closes the reverse-tabnabbing
                    hole that `target="_blank"` opens. */}
                <a href={href} target="_blank" rel="noopener noreferrer">{text}</a>
                <span className={styles.chevCell}>›</span>
            </>
        );
    }
    return <>{text}</>;
}

function acceptedNote(acceptance: OpsAcceptance): string {
    const when = acceptance.accepted_at
        ? new Date(acceptance.accepted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : "";
    return `Accepted by ${acceptance.accepted_by}${when ? ` on ${when}` : ""}: ${acceptance.reason}`;
}

function cellTitle(column: Column, value: PayloadValue): string | undefined {
    if (column.nowrap || column.kind === "badge") return undefined;
    const text = isToned(value) ? value.t : value;
    return typeof text === "string" && text ? text : undefined;
}

interface Props {
    tileKey: string;
    count: number;
    filters: OpsFilters;
    emptyTitle: string;
    /** The rule as maths — see OpsTile.formula. */
    formula?: string;
    /** The caveat shown beneath it — see OpsTile.method. */
    method?: string;
    /** What each badge colour means — see OpsTile.tiers. */
    tiers?: string[][];
    /** Whether a row can be accepted as fine (MPN-2010) — see OpsTile.acceptable. */
    acceptable?: boolean;
}

export function OpsTileCard({
    tileKey, count, filters, emptyTitle, formula, method, tiers, acceptable = false,
}: Props) {
    const spec = TILE_SPECS[tileKey];
    const [tile, setTile] = useState<OpsTile | null>(null);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState(false);

    // Accepting a flag as fine (MPN-2010). The list shows either the active flags or the
    // ones already accepted, never both, so an accepted row cannot be mistaken for a live one.
    const canAccept = acceptable;
    const [showAccepted, setShowAccepted] = useState(false);
    const [reload, setReload] = useState(0);
    const [acceptingId, setAcceptingId] = useState<number | null>(null);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [acceptError, setAcceptError] = useState("");

    const startAccept = (subjectId: number) => {
        setAcceptingId(subjectId);
        setReason("");
        setAcceptError("");
    };

    const saveAccept = async () => {
        if (acceptingId === null || !reason.trim()) return;
        setSaving(true);
        setAcceptError("");
        try {
            await new CommandAcceptOpsFlag().run(tileKey, acceptingId, reason.trim());
            setAcceptingId(null);
            setReload((n) => n + 1);
        } catch (error) {
            setAcceptError(error instanceof Error ? error.message : "Couldn't accept this flag.");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setFailed(false);
        new CommandGetOpsTile()
            .run(tileKey, filters, showAccepted)
            .then((result) => {
                if (!cancelled) setTile(result);
            })
            .catch(() => {
                // Without this the card sits blank forever: the empty state is gated on
                // a resolved response, so a failure is indistinguishable from loading.
                if (!cancelled) {
                    setTile({ tile_key: tileKey, count: 0, total: 0, rows: [] });
                    setFailed(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [tileKey, filters, showAccepted, reload]);

    if (!spec) return null;

    const all: OpsAlertRow[] = tile?.rows ?? [];
    const shown = open ? all : all.slice(0, COLLAPSED_ROWS);
    // The tile's own count is authoritative once it has loaded; `count` from the summary
    // is only a placeholder for the moment before that, and is 0 if the summary failed.
    const matched = tile?.count ?? count;
    // Rows this tab holds with the filter bar ignored. A filtered card reports both, so a
    // count that dropped from 6 to 4 reads as the filter working rather than as rows
    // going missing (MPN-1840).
    const unfiltered = tile?.total ?? matched;
    const narrowed = filtersActive(filters) && unfiltered > matched;

    let countText;
    if (showAccepted) {
        countText = `${matched} accepted`;
    } else if (spec.countLabel) {
        countText = spec.countLabel(matched);
    } else if (narrowed) {
        countText = `${matched} of ${unfiltered} ${plural(spec.unit)}`;
    } else {
        countText = unitLabel(matched, spec.unit);
    }

    return (
        <>
            {spec.note && <p className={styles.tileNote}>{spec.note}</p>}
            <div
                className={[
                    styles.card,
                    spec.spanFull ? styles.wide : "",
                ].join(" ")}
            >
                <div className={styles.cardHead}>
                    <h3 className={styles.cardTitle}>{spec.title}</h3>
                    <InfoTip text={spec.tip} formula={formula} method={method} tiers={tiers} />
                    <span className={styles.cardCount}>{countText}</span>
                    {canAccept && (
                        <button
                            type="button"
                            className={styles.acceptToggle}
                            aria-pressed={showAccepted}
                            onClick={() => {
                                setShowAccepted(!showAccepted);
                                setAcceptingId(null);
                            }}
                        >
                            {showAccepted ? "Show flagged" : "Show accepted"}
                        </button>
                    )}
                </div>

                {/* The description is PRINTED, not only tucked in the tooltip. A table
                    that needs explaining should explain itself on sight; the icon stays
                    because it also carries the calculation, which is reference rather
                    than something to read every time. */}
                {spec.tip && <p className={styles.cardDesc}>{spec.tip}</p>}

                <div
                    className={[
                        styles.scroll,
                        open ? styles.scrollOpen : "",
                        all.length === 0 ? styles.scrollEmpty : "",
                    ].join(" ")}
                >
                    {all.length > 0 && (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {spec.columns.map((col) => (
                                        <th
                                            key={col.key}
                                            className={col.align === "right" ? styles.alignRight : undefined}
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                    {canAccept && <th><span className={styles.srOnly}>Accept</span></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Several tiles emit more than one row per subject —
                                    low reach and spend drop-off are per network, conversion
                                    changes per day — so the subject id alone is not unique. */}
                                {shown.map((row, i) => {
                                    // Once per row, not once per cell: every column was
                                    // recomputing the same destination, and the lookup
                                    // walks the engagement type's workflow definition.
                                    const href = rowHref(row, tileKey);
                                    const accepting = acceptingId === row.subject_id;
                                    return (
                                    <Fragment key={`${row.subject_type}-${row.subject_id}-${i}`}>
                                    <tr>
                                        {spec.columns.map((col) => (
                                            <td
                                                key={col.key}
                                                // The mockup carries the cell's own text as
                                                // a tooltip on every WRAPPING text column and
                                                // on none of the nowrap or badge ones — those
                                                // are short by construction, these get clipped.
                                                title={cellTitle(col, row.payload[col.key])}
                                                className={[
                                                    CELL_CLASS[col.kind],
                                                    col.nowrap ? styles.nowrap : "",
                                                    col.align === "right" ? styles.alignRight : "",
                                                ].join(" ")}
                                            >
                                                <Cell
                                                    column={col}
                                                    value={row.payload[col.key]}
                                                    href={href}
                                                    pct={
                                                        typeof row.payload.progress_pct === "number"
                                                            ? row.payload.progress_pct
                                                            : undefined
                                                    }
                                                />
                                            </td>
                                        ))}
                                        {canAccept && (
                                            <td className={styles.acceptCell}>
                                                {showAccepted && row.acceptance ? (
                                                    <span className={styles.acceptNote}>{acceptedNote(row.acceptance)}</span>
                                                ) : (
                                                    !showAccepted && !accepting && (
                                                        <button
                                                            type="button"
                                                            className={styles.acceptBtn}
                                                            onClick={() => startAccept(row.subject_id)}
                                                        >
                                                            Accept
                                                        </button>
                                                    )
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    {accepting && (
                                        <tr>
                                            <td colSpan={spec.columns.length + 1} className={styles.acceptForm}>
                                                <label htmlFor={`accept-${tileKey}-${row.subject_id}`} className={styles.acceptLabel}>
                                                    Why is this fine? The reason is the record, and the row
                                                    comes back if it changes.
                                                </label>
                                                <textarea
                                                    id={`accept-${tileKey}-${row.subject_id}`}
                                                    className={styles.acceptInput}
                                                    rows={2}
                                                    value={reason}
                                                    onChange={(e) => setReason(e.target.value)}
                                                />
                                                {acceptError && <p className={styles.acceptError} role="alert">{acceptError}</p>}
                                                <div className={styles.acceptActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.acceptBtn}
                                                        disabled={saving || !reason.trim()}
                                                        onClick={saveAccept}
                                                    >
                                                        {saving ? "Accepting…" : "Accept"}
                                                    </button>
                                                    <button type="button" className={styles.expandBtn} onClick={() => setAcceptingId(null)}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    {tile !== null && all.length === 0 && (
                        <EmptyState
                            title={
                                failed ? "Couldn't load this table. Refresh to try again."
                                    : showAccepted ? "Nothing accepted here." : emptyTitle
                            }
                            icon={failed ? "bi-exclamation-triangle" : spec.emptyIcon}
                        />
                    )}
                </div>

                {all.length > COLLAPSED_ROWS && (
                    <div className={styles.expand}>
                        <button type="button" className={styles.expandBtn} onClick={() => setOpen(!open)}>
                            {open ? "Show less ▴" : `Show all ${matched} ▾`}
                        </button>
                        {/* The API caps a tile's rows; the header count is uncapped, so
                            without this an expanded card silently shows fewer rows than it
                            claims. Distinct from the filtered "4 of 6" above. */}
                        {open && matched > all.length && (
                            <span className={styles.expandNote}>
                                Showing the first {all.length} of {matched}.
                            </span>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
